import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Tab,
  Tabs,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Storefront,
  CheckCircle,
  Money,
  Hourglass,
  Search,
  MoreVert,
  EventAvailable,
  EventIcon,
  CancelIcon,
} from '../../components/ui/Icons';
import {
  adminListSubscriptions,
  adminMarkSubscriptionPaid,
  adminCancelSubscription,
  adminExtendTrial,
} from '../../services/api';
import { toast } from '../../lib/toast';
import { formatCurrency } from '../../utils/formatCurrency';
import AppShell from '../../components/layout/AppShell';
import PageHeader from '../../components/layout/PageHeader';
import KpiCard from '../../components/ui/KpiCard';
import StatusPill from '../../components/ui/StatusPill';
import DataTable from '../../components/ui/DataTable';

const FILTERS = ['all', 'active', 'trialing', 'expired', 'past_due', 'canceled', 'none'];
const MS_PER_DAY = 86400000;

const isPast = (d) => !!d && new Date(d).getTime() <= Date.now();

const effectiveStatus = (sub) => {
  if (!sub) return 'none';
  if (sub.status === 'trialing' && isPast(sub.trial_ends_at)) return 'expired';
  if (sub.status === 'active' && isPast(sub.current_period_end)) return 'expired';
  return sub.status;
};

const formatDate = (d, locale) =>
  !d ? '—' : new Date(d).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });

const daysFromNow = (d) => (!d ? null : Math.ceil((new Date(d).getTime() - Date.now()) / MS_PER_DAY));

const relativeDaysText = (d, locale) => {
  if (!d) return null;
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    return rtf.format(daysFromNow(d), 'day');
  } catch (_) {
    return null;
  }
};

const avatarInitials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
};

const avatarHue = (name) => {
  if (!name) return 220;
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
};

const AdminSubscriptions = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'es' ? 'es-ES' : 'en-US';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [extendDialog, setExtendDialog] = useState(null);
  const [extendDays, setExtendDays] = useState('14');
  const [actionMenu, setActionMenu] = useState({ anchor: null, row: null });

  const refresh = async () => {
    try {
      setLoading(true);
      const res = await adminListSubscriptions();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load subscriptions', err);
      toast.error(t('admin.subscriptions.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = rows.filter((r) => {
      const eff = effectiveStatus(r.subscription);
      if (filter !== 'all') {
        if (filter === 'expired' && eff !== 'expired') return false;
        if (filter !== 'expired' && eff !== filter) return false;
      }
      if (!q) return true;
      const hay = `${r.restaurant?.name || ''} ${r.manager?.name || ''} ${r.manager?.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
    const sortKey = (r) => {
      const eff = effectiveStatus(r.subscription);
      const sub = r.subscription;
      if (eff === 'past_due' || eff === 'unpaid' || eff === 'incomplete') return 0;
      if (eff === 'trialing') return Math.max(0, daysFromNow(sub?.trial_ends_at) ?? 0);
      if (eff === 'active') return 1000 + Math.max(0, daysFromNow(sub?.current_period_end) ?? 0);
      if (eff === 'expired' || eff === 'canceled' || eff === 'incomplete_expired') return 10000;
      return 20000;
    };
    return [...matched].sort((a, b) => {
      const diff = sortKey(a) - sortKey(b);
      if (diff !== 0) return diff;
      return (a.restaurant?.name || '').localeCompare(b.restaurant?.name || '');
    });
  }, [rows, filter, search]);

  const counts = useMemo(() => {
    const out = { all: rows.length };
    for (const f of FILTERS.filter((f) => f !== 'all')) out[f] = 0;
    for (const r of rows) {
      const eff = effectiveStatus(r.subscription);
      if (eff in out) out[eff] += 1;
    }
    return out;
  }, [rows]);

  const summary = useMemo(() => {
    let active = 0, trialing = 0, mrrCents = 0;
    for (const r of rows) {
      const eff = effectiveStatus(r.subscription);
      if (eff === 'active') {
        active += 1;
        const monthly = r.subscription?.plan?.monthly_price_cents || 0;
        const yearly = r.subscription?.plan?.yearly_price_cents || 0;
        if (r.subscription?.billing_interval === 'yearly') mrrCents += Math.round(yearly / 12);
        else mrrCents += monthly;
      } else if (eff === 'trialing') {
        trialing += 1;
      }
    }
    return {
      totalRestaurants: rows.length,
      active,
      trialing,
      mrr: mrrCents / 100,
    };
  }, [rows]);

  const planCurrency = useMemo(() => {
    for (const r of rows) {
      const code = r.subscription?.plan?.currency;
      if (code && typeof code === 'string') return code.toUpperCase();
    }
    return 'USD';
  }, [rows]);

  const askConfirm = (type, restaurantId, label, payload) => {
    setConfirm({ type, restaurantId, label, payload });
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    const { type, restaurantId, payload } = confirm;
    setConfirm(null);
    try {
      setBusyId(restaurantId);
      if (type === 'mark') {
        await adminMarkSubscriptionPaid(restaurantId, payload.interval);
        toast.success(t('admin.subscriptions.toast.marked'));
      } else if (type === 'cancel') {
        await adminCancelSubscription(restaurantId);
        toast.success(t('admin.subscriptions.toast.canceled'));
      }
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.subscriptions.errors.actionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const submitExtend = async () => {
    if (!extendDialog) return;
    const days = parseInt(extendDays, 10);
    if (!Number.isFinite(days) || days <= 0) {
      toast.error(t('admin.subscriptions.errors.invalidDays'));
      return;
    }
    const { restaurantId } = extendDialog;
    setExtendDialog(null);
    try {
      setBusyId(restaurantId);
      await adminExtendTrial(restaurantId, days);
      toast.success(t('admin.subscriptions.toast.trialExtended', { count: days }));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.subscriptions.errors.actionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const rowMenuItems = (row) => {
    if (!row) return [];
    const eff = effectiveStatus(row.subscription);
    const sub = row.subscription;
    const restaurantId = row.restaurant.id;
    const name = row.restaurant.name || '';
    const items = [
      {
        key: 'markMonthly',
        icon: <EventAvailable size={18} />,
        label: t('admin.subscriptions.action.markMonthly'),
        onClick: () => askConfirm('mark', restaurantId, name, { interval: 'monthly' }),
      },
      {
        key: 'markYearly',
        icon: <EventAvailable size={18} />,
        label: t('admin.subscriptions.action.markYearly'),
        onClick: () => askConfirm('mark', restaurantId, name, { interval: 'yearly' }),
      },
    ];
    if (eff === 'trialing' || eff === 'expired') {
      items.push({
        key: 'extendTrial',
        icon: <EventIcon size={18} />,
        label: t('admin.subscriptions.action.extendTrial'),
        onClick: () => { setExtendDialog({ restaurantId, name }); setExtendDays('14'); },
      });
    }
    if (sub && eff !== 'canceled' && eff !== 'incomplete_expired') {
      items.push({
        key: 'cancel',
        icon: <CancelIcon size={18} />,
        label: t('admin.subscriptions.action.cancel'),
        danger: true,
        onClick: () => askConfirm('cancel', restaurantId, name),
      });
    }
    return items;
  };

  const columns = [
    {
      id: 'restaurant',
      header: t('admin.subscriptions.col.restaurant'),
      render: (row) => {
        const name = row.restaurant?.name || '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: `hsl(${avatarHue(name)}, 65%, 92%)`,
                color: `hsl(${avatarHue(name)}, 55%, 35%)`,
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {avatarInitials(name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: 14, lineHeight: 1.3 }}>
                {name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                #{row.restaurant?.id}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'plan',
      header: t('admin.subscriptions.col.interval'),
      hideOnSm: true,
      render: (row) => {
        const sub = row.subscription;
        if (!sub?.billing_interval) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>;
        const planName = sub.plan?.name || t(`admin.subscriptions.interval.${sub.billing_interval}`);
        return (
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}>
              {planName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t(`admin.subscriptions.interval.${sub.billing_interval}`)}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'status',
      header: t('admin.subscriptions.col.status'),
      render: (row) => {
        const eff = effectiveStatus(row.subscription);
        return <StatusPill status={eff} label={t(`admin.subscriptions.status.${eff}`, eff)} dot />;
      },
    },
    {
      id: 'dates',
      header: t('admin.subscriptions.col.dates'),
      hideOnSm: true,
      render: (row) => {
        const sub = row.subscription;
        let urgencyDate = null;
        let urgencyLabel = null;
        if (sub?.status === 'trialing') {
          urgencyDate = sub.trial_ends_at;
          urgencyLabel = t('admin.subscriptions.col.trialEnds');
        } else if (sub?.status === 'active') {
          urgencyDate = sub.current_period_end;
          urgencyLabel = sub.cancel_at_period_end
            ? t('admin.subscriptions.col.endsOn')
            : t('admin.subscriptions.col.renewsOn');
        }
        if (!urgencyDate) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>;
        const days = daysFromNow(urgencyDate);
        const relText = relativeDaysText(urgencyDate, locale);
        const overdue = days != null && days < 0;
        const soon = days != null && days >= 0 && days <= 7;
        return (
          <Box>
            <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
              {formatDate(urgencyDate, locale)}
            </Typography>
            {relText && (
              <Typography
                variant="caption"
                sx={{
                  color: overdue ? 'error.main' : soon ? 'warning.main' : 'text.secondary',
                  fontWeight: overdue || soon ? 600 : 400,
                }}
              >
                {urgencyLabel} · {relText}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'manager',
      header: t('admin.subscriptions.col.manager'),
      hideOnSm: true,
      render: (row) =>
        row.manager ? (
          <Box>
            <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{row.manager.name}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{row.manager.email}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('admin.subscriptions.noManager')}
          </Typography>
        ),
    },
    {
      id: 'actions',
      header: t('admin.subscriptions.col.actions'),
      align: 'right',
      width: 80,
      render: (row) => (
        <IconButton
          size="small"
          disabled={busyId === row.restaurant.id}
          aria-label={t('admin.subscriptions.col.actions')}
          onClick={(e) => {
            e.stopPropagation();
            setActionMenu({ anchor: e.currentTarget, row });
          }}
        >
          <MoreVert size={20} />
        </IconButton>
      ),
    },
  ];

  return (
    <AppShell>
      <PageHeader
        title={t('admin.subscriptions.title')}
        subtitle={t('admin.subscriptions.subtitle')}
      />

      {/* KPI row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2.5,
          mb: 4,
        }}
      >
        <KpiCard
          icon={<Storefront size={22} />}
          iconTone="primary"
          label={t('sidebar.restaurants', 'Total Restaurants')}
          value={summary.totalRestaurants}
        />
        <KpiCard
          icon={<CheckCircle size={22} />}
          iconTone="success"
          label={t('admin.subscriptions.stats.active')}
          value={summary.active}
        />
        <KpiCard
          icon={<Money size={22} />}
          iconTone="info"
          label={t('admin.subscriptions.stats.mrr')}
          value={formatCurrency(summary.mrr, planCurrency, locale)}
        />
        <KpiCard
          icon={<Hourglass size={22} />}
          iconTone="secondary"
          label={t('admin.subscriptions.stats.trialing')}
          value={summary.trialing}
        />
      </Box>

      {/* Tabs row */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={filter}
          onChange={(_, v) => setFilter(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {FILTERS.map((f) => (
            <Tab
              key={f}
              value={f}
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <span>{t(`admin.subscriptions.filter.${f}`)}</span>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'text.secondary',
                      backgroundColor: 'action.hover',
                      padding: '2px 8px',
                      borderRadius: 9999,
                    }}
                  >
                    {counts[f] ?? 0}
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Toolbar — search */}
      <Box sx={{ mb: 2.5 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('admin.subscriptions.searchPlaceholder')}
          size="small"
          sx={{ width: { xs: '100%', sm: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>
                  <Search size={18} />
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table */}
      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        empty={{
          icon: '📭',
          title: t('admin.subscriptions.empty'),
        }}
        getRowKey={(row) => row.restaurant.id}
        ariaLabel={t('admin.subscriptions.title')}
      />

      {/* Action menu */}
      <Menu
        anchorEl={actionMenu.anchor}
        open={Boolean(actionMenu.anchor)}
        onClose={() => setActionMenu({ anchor: null, row: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rowMenuItems(actionMenu.row).map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => {
              setActionMenu({ anchor: null, row: null });
              item.onClick();
            }}
            sx={item.danger ? { color: 'error.main' } : undefined}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Confirm dialog (mark paid / cancel) */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm && t(`admin.subscriptions.confirm.${confirm.type}.title`)}</DialogTitle>
        <DialogContent>
          {confirm && (
            <Typography sx={{ m: 0 }}>
              {t(`admin.subscriptions.confirm.${confirm.type}.message`, {
                name: confirm.label,
                interval: confirm.payload?.interval
                  ? t(`admin.subscriptions.interval.${confirm.payload.interval}`)
                  : '',
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirm(null)}>{t('common.cancel')}</Button>
          <Button
            onClick={runConfirmed}
            variant="contained"
            color={confirm?.type === 'cancel' ? 'error' : 'primary'}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend trial dialog */}
      <Dialog open={!!extendDialog} onClose={() => setExtendDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('admin.subscriptions.extendDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {extendDialog && t('admin.subscriptions.extendDialog.message', { name: extendDialog.name })}
          </Typography>
          <TextField
            type="number"
            inputProps={{ min: 1, max: 365 }}
            value={extendDays}
            onChange={(e) => setExtendDays(e.target.value)}
            autoFocus
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setExtendDialog(null)}>{t('common.cancel')}</Button>
          <Button onClick={submitExtend} variant="contained" color="primary">
            {t('admin.subscriptions.action.extendTrial')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
};

export default AdminSubscriptions;
